import { inject, injectable } from "@launchtray/tsyringe-async";
import { IPreloaderModel } from "../models/IPreloaderModel";
import { IPreloaderController } from "./IPreloaderController";
import { IContextResolver } from "../../../composition/IContextResolver";
import { LoginModule } from "../../popups/login/LoginModule";
import { ILogger } from "../../../logger/ILogger";
import { IAuthModel } from "../../popups/login/models/IAuthModel";
import { Web3Model } from "../../../../web3/models/Web3Model";
import { GameInfo } from "by/gamefactory/analytics/GameInfo";
import { IConnectionModel } from "by/gamefactory/cityversetycoon/networking/IConnectionModel";
import { IApplicationModel } from "../../common/models/IApplicationModel";

@injectable()
export class PreloaderController implements IPreloaderController {
    private readonly contextResolver: IContextResolver;
    private readonly preloaderModel: IPreloaderModel;
    private readonly authModel: IAuthModel;
    private readonly connectionModel: IConnectionModel;
    private readonly applicationModel: IApplicationModel;
    private readonly web3Model: Web3Model;
    private readonly gameInfo: GameInfo;
    private readonly logger: ILogger;

    public constructor(
        @inject("IContextResolver") contextResolver: IContextResolver,
        @inject("IPreloaderModel") preloaderModel: IPreloaderModel,
        @inject("IAuthModel") authModel: IAuthModel,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        @inject("IApplicationModel") applicationModel: IApplicationModel,
        web3Model: Web3Model,
        gameInfo: GameInfo,
        @inject("ILogger") logger: ILogger
    ) {
        this.contextResolver = contextResolver;
        this.preloaderModel = preloaderModel;
        this.authModel = authModel;
        this.connectionModel = connectionModel;
        this.applicationModel = applicationModel;
        this.web3Model = web3Model;
        this.gameInfo = gameInfo;
        this.logger = logger;
    }

    public initializeGame(): void {
        this.logger.info(`Application build version: ${this.gameInfo.gameVersion}, build number: ${this.gameInfo.buildNumber}`);
        this.preloaderModel.runInitializers();
    }

    public async checkAndUpdateAuthState(): Promise<void> {
        try {
            if (!this.connectionModel.connected) {
                await this.showMainLoginPopup();
            }
            await this.web3Model.autoConnect();
            if (await this.authModel.refreshToken()) {
                await this.authModel.updateAccountProgress();
                return;
            }
            await this.showMainLoginPopup();
        }
        catch (e: unknown) {
            this.logger.debug(`Exception on update auth state: ${e}`);
            if (!this.connectionModel.connected) {
                await this.showMainLoginPopup();
            }
        }
    }

    private async showMainLoginPopup(): Promise<void> {
        const loginModule = await this.contextResolver.resolve(LoginModule);
        this.applicationModel.gameLoadedHandle();
        await loginModule.start();
    }
}
